import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./value-picker-dialog.i18n";

/** The caller's text; the dialog owns the rest of its words. */
export type ValuePickerLabels = {
  /** Dialog title, e.g. "Select Vulnerable Investor". */
  title: string;
  /** Description under the title, given the live selection size. */
  description: (count: number) => string;
  /** Overrides the generic search placeholder, e.g. "Search users". */
  search?: string;
  /** Overrides the generic empty-result text, e.g. "No users found". */
  noOptionsFound?: string;
};

export type ValuePickerOption = {
  value: string;
  label: string;
  /** Secondary column — ISIN, org number. Right-aligned in mono (design 14f). */
  meta?: string;
};

/** Splits a label around the matched term so the match can be weighted. */
function highlight(label: string, term: string) {
  if (!term) return <>{label}</>;
  const at = label.toLowerCase().indexOf(term.toLowerCase());
  if (at < 0) return <>{label}</>;
  return (
    <>
      {label.slice(0, at)}
      <span className="font-semibold">{label.slice(at, at + term.length)}</span>
      {label.slice(at + term.length)}
    </>
  );
}

/**
 * Multi-select value picker dialog (client-view custom-filter style):
 * search input, Select All / Clear All, scrollable option list, and a footer
 * with Cancel + Save. Shared by client views and the risk-model cap value
 * selection.
 *
 * Design 14f — 36px rows, the match weighted inside the label, the option's
 * secondary column right-aligned in mono so a column of ISINs reads as a
 * column. The search takes focus on open; you arrive here to type.
 */
export function ValuePickerDialog({
  open,
  options,
  selectedValues,
  labels,
  onClose,
  onSave,
}: {
  open: boolean;
  options: ValuePickerOption[];
  selectedValues: string[];
  labels: ValuePickerLabels;
  onClose: () => void;
  onSave: (values: string[]) => void;
}) {
  const t = useT(T);
  const [values, setValues] = useState<string[]>(selectedValues);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValues(selectedValues);
    setSearch("");
  }, [selectedValues, open]);

  const term = search.trim();
  const filteredOptions = useMemo(
    () =>
      options.filter((opt) =>
        `${opt.label} ${opt.meta ?? ""}`.toLowerCase().includes(term.toLowerCase()),
      ),
    [options, term],
  );

  const handleToggle = (value: string) => {
    setValues((current) =>
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    );
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
          <DialogDescription>{labels.description(values.length)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <SearchInput
            ref={searchRef}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- the dialog exists to be searched
            autoFocus
            placeholder={labels.search ?? t.search}
            value={search}
            onValueChange={setSearch}
          />

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              // Merged, not replaced: with a search term active this button
              // reads as "select all of these", and replacing would silently
              // drop everything picked under an earlier term.
              onClick={() =>
                setValues((current) => [
                  ...new Set([...current, ...filteredOptions.map((o) => o.value)]),
                ])
              }
            >
              {t.selectAll}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setValues([])}>
              {t.clearAll}
            </Button>
          </div>

          <div className="max-h-[400px] overflow-y-auto rounded-md border p-1">
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className="flex h-9 items-center gap-2 rounded-sm px-2 hover:bg-accent"
              >
                <Checkbox
                  id={`value-${option.value}`}
                  checked={values.includes(option.value)}
                  onCheckedChange={() => handleToggle(option.value)}
                />
                <Label
                  htmlFor={`value-${option.value}`}
                  className="min-w-0 flex-1 cursor-pointer truncate font-normal"
                >
                  {highlight(option.label, term)}
                </Label>
                {option.meta && (
                  <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                    {option.meta}
                  </span>
                )}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                {labels.noOptionsFound ?? t.noOptionsFound}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <DialogCancel>{t.cancel}</DialogCancel>
          <Button type="button" onClick={() => onSave(values)}>
            {t.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
